import React, { useState, useEffect } from 'react'
import {
  Box, IconButton, Menu, MenuItem,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import GridMoreVertIcon from '@mui/icons-material/MoreVert'
import PropTypes from 'prop-types'
import {
  showErrorNotification,
  showSuccessNotification
} from '../../../../../../redux/actions'
import { useFetchNetSuiteInvoiceQuery } from '../../../../../../redux/slices/integrationsSlice'
import { DEVELOPER_ACCESS } from '../../../../../../auth/policies'
import { hasPolicy } from '../../../../../../redux/selectors'
import { PAYMENT_INFO_TYPE } from '../../../../../../constants/partners'

function InvoiceOptionsMenu({ row, onClickSyncInvoice, onClickViewInvoice, onClickSetPaymentProfileOverride, paymentProfileType }) {
  const [menuAnchor, setMenuAnchor] = useState(null)
  const dispatch = useDispatch()
  const [canViewInNS, setCanViewInNS] = useState(false)

  const success = (message) => {
    dispatch(showSuccessNotification(message))
  }
  const failure = (message) => {
    dispatch(showErrorNotification(message))
  }

  const handleMenuOpen = (event) => {
    event.stopPropagation()
    setMenuAnchor(event.currentTarget)
  }

  const {
    data: invoiceResponse,
    isFetching: netSuiteInvoiceLoading,
    isError: invoiceError,
  } = useFetchNetSuiteInvoiceQuery(row.id, { skip: !Boolean(menuAnchor), refetchOnMountOrArgChange: false })

  useEffect(() => {
    setCanViewInNS((invoiceResponse || {}).invoice && !invoiceError)
  }, [invoiceResponse, invoiceError])

  const handleMenuClose = (event) => {
    event?.stopPropagation()
    setMenuAnchor(null)
  }

  const handleSyncInvoice = (event) => {
    event.stopPropagation()
    onClickSyncInvoice(row.id)
      .unwrap()
      .then((res) => 
        res.success ? success(res.errorMessage)
        : failure(res.errorMessage))
      .catch((error) => {
        failure(error?.message || "Failed to sync invoice due to a network or server error.");
      });
    handleMenuClose(event)
  }

  const handleViewInvoice = (event) => {
    onClickViewInvoice(row.id)
    handleMenuClose(event)
  }

  const handleSetBillingType = (event, paymentType) => {
    event.stopPropagation()
    onClickSetPaymentProfileOverride(row.id, paymentType)
      .unwrap()
      .then((res) =>
        res.success ? success(`Invoice switched to ${paymentType} billing.`) : failure(res.message || `Failed to switch to ${paymentType} billing.`))
      .catch((error) => {
        failure(error?.data?.message || error?.message || `Failed to switch to ${paymentType} billing. (${error?.status ?? 'error'})`)
      })
    handleMenuClose(event)
  }

  const hasDeveloperAccess = useSelector(hasPolicy(DEVELOPER_ACCESS))
  const normalizedProfileType = paymentProfileType?.toLowerCase()
  const isOpen = row.status?.toLowerCase() === 'open'

  return (
    <Box>
      <IconButton style={{ paddingTop: '9px' }} onClick={handleMenuOpen}>
        <GridMoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {
          netSuiteInvoiceLoading ? (
            <MenuItem data-gxid="loadingInvoice">Loading...</MenuItem>
          ) : (
            <>
              
              <MenuItem onClick={handleViewInvoice} data-gxid="viewInvoice" disabled={!canViewInNS}>
                View invoice in NetSuite
              </MenuItem>
              {hasDeveloperAccess && (
              <MenuItem onClick={handleSyncInvoice} data-gxid="syncInvoice" disabled={canViewInNS}>
                Sync Invoice To NetSuite
              </MenuItem>
              )}
              {isOpen && normalizedProfileType === PAYMENT_INFO_TYPE.shopify.toLowerCase() && (
                <MenuItem onClick={(e) => handleSetBillingType(e, PAYMENT_INFO_TYPE.manual)} data-gxid="setManualBilling">
                  Switch to manual billing
                </MenuItem>
              )}
              {isOpen && normalizedProfileType === PAYMENT_INFO_TYPE.manual.toLowerCase() && (
                <MenuItem onClick={(e) => handleSetBillingType(e, PAYMENT_INFO_TYPE.shopify)} data-gxid="setShopifyBilling">
                  Switch to Shopify billing
                </MenuItem>
              )}
            </>
          )
        }
      </Menu>
    </Box>
  )
}

InvoiceOptionsMenu.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  onClickSyncInvoice: PropTypes.func.isRequired,
  onClickViewInvoice: PropTypes.func.isRequired,
  onClickSetPaymentProfileOverride: PropTypes.func.isRequired,
  paymentProfileType: PropTypes.string,
}

export default InvoiceOptionsMenu
